import { redis } from './redis'
import type { SupportedLocale } from '../translation/types'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/nextjs'

// Increase session TTL (especially for last-minute bookings)
const SESSION_TTL = 60 * 60 // 60 minutes in seconds for regular sessions
const LAST_MINUTE_SESSION_TTL = 60 * 90 // 90 minutes for last-minute bookings

// Session ID validation regex (nanoid produces a-zA-Z0-9_- characters)
const SESSION_ID_REGEX = /^[a-zA-Z0-9_-]{21}$/

export interface BookingSession {
  id: string
  locale: SupportedLocale
  step: 'treatment' | 'doctor' | 'time' | 'patient' | 'confirm' | 'slots'
  data: {
    activityId?: number
    treatmentId?: number
    doctorId?: number
    doctorName?: string
    date?: string
    time?: string
    isLastMinute?: boolean
    activityName?: string
    discount?: number
    patient?: {
      firstName: string
      lastName: string
      email: string
      phone: string
      fiscalCode?: string
      isForeigner: boolean
      gender?: 'M' | 'F'
      birthDate?: {
        day: number
        month: number
        year: number
      }
      birthCountry?: string
      birthCity?: string
      privacyForRegistration: boolean
      privacyForMedicalBooking: boolean
      privacyForCommercialUse?: boolean
      subjectId?: number
    }
  }
  metadata: {
    createdAt: string
    updatedAt: string
    expiresAt: string
  }
}

/**
 * Validates a session ID format
 * @param id The session ID to validate
 * @returns True if session ID format is valid
 */
export function isValidSessionId(id: string): boolean {
  return SESSION_ID_REGEX.test(id)
}

/**
 * Creates a new booking session
 * @param locale User's locale
 * @param step Initial booking step
 * @param isLastMinute Whether this is a last-minute booking (affects TTL)
 * @returns The created booking session
 */
export async function createBookingSession(
  locale: SupportedLocale,
  step: BookingSession['step'] = 'treatment',
  isLastMinute = false,
): Promise<BookingSession> {
  try {
    const sessionId = nanoid()
    // Use longer TTL for last-minute bookings
    const ttl = isLastMinute ? LAST_MINUTE_SESSION_TTL : SESSION_TTL

    const session: BookingSession = {
      id: sessionId,
      locale,
      step,
      data: {
        isLastMinute: isLastMinute || undefined, // Only set if true
      },
      metadata: {
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + ttl * 1000).toISOString(),
      },
    }

    const key = `booking:${session.id}`
    await redis.set(key, session, { ex: ttl })
    console.log(`Created booking session: ${sessionId}, last-minute: ${isLastMinute}, TTL: ${ttl}s`)
    return session
  } catch (error) {
    console.error('Error creating booking session:', error)
    Sentry.captureException(error, {
      extra: { function: 'createBookingSession', locale, step, isLastMinute },
    })
    throw new Error('Failed to create booking session')
  }
}

/**
 * Retrieves a booking session with retry logic
 * @param id Session ID
 * @returns The booking session or null if not found
 */
export async function getBookingSession(id: string): Promise<BookingSession | null> {
  if (!SESSION_ID_REGEX.test(id)) {
    console.warn('Invalid session ID format:', id)
    return null
  }

  try {
    // Attempt to get the session with retry logic
    const maxRetries = 2
    let retryCount = 0
    let sessionData = null

    while (retryCount <= maxRetries) {
      try {
        sessionData = await redis.get<BookingSession>(`booking:${id}`)

        // If we got data, break the retry loop
        if (sessionData) {
          break
        }

        // Only log the first retry to avoid spamming logs
        if (retryCount === 0) {
          console.warn(`Redis returned null for session ID: ${id}, attempt ${retryCount + 1}/${maxRetries + 1}`)
        }

        // Increment retry count and wait before next attempt
        retryCount++
        if (retryCount <= maxRetries) {
          // Exponential backoff: wait longer between retries
          await new Promise((resolve) => setTimeout(resolve, 300 * retryCount))
        }
      } catch (error) {
        console.error(`Error fetching session on retry ${retryCount}:`, error)
        retryCount++
        if (retryCount <= maxRetries) {
          await new Promise((resolve) => setTimeout(resolve, 300 * retryCount))
        } else {
          throw error // Re-throw if we're out of retries
        }
      }
    }

    if (!sessionData) {
      // Log warning and capture in Sentry for monitoring
      console.warn(`Session not found after ${maxRetries + 1} attempts:`, id)
      Sentry.captureMessage(`Session not found in Redis: ${id}`, {
        level: 'warning',
        extra: { sessionId: id },
      })
      return null
    }

    // Add back session TTL extension after the while loop
    if (sessionData) {
      // Extend session TTL on successful retrieval
      const ttl = sessionData.data.isLastMinute ? LAST_MINUTE_SESSION_TTL : SESSION_TTL
      // Update expiry without changing the session
      try {
        await redis.expire(`booking:${id}`, ttl)
      } catch (expireError) {
        console.error('Error extending session TTL:', expireError)
        // Non-fatal, continue even if expire fails
      }
    }

    // Return the session if found
    return sessionData
  } catch (error) {
    console.error('Error getting booking session:', error)
    Sentry.captureException(error, {
      extra: { sessionId: id, context: 'getBookingSession' },
    })
    return null
  }
}

/**
 * Updates a booking session
 * @param id Session ID
 * @param updates Fields to update
 * @returns The updated session or null if session not found
 */
export async function updateBookingSession(
  id: string,
  updates: {
    treatmentId?: number
    activityId?: number
    doctorId?: number
    doctorName?: string
    isLastMinute?: boolean
    discount?: number
    step?: BookingSession['step']
    activityName?: string
    date?: string
    time?: string
    patient?: BookingSession['data']['patient']
  },
): Promise<BookingSession | null> {
  try {
    // Validate session ID format first
    if (!isValidSessionId(id)) {
      console.error(`Invalid session ID format in update: ${id}`)
      return null
    }

    const session = await getBookingSession(id)
    if (!session) {
      console.log(`Cannot update - session not found: ${id}`)
      return null
    }

    // Reset dependent fields when changing treatment/activity
    if (updates.treatmentId || updates.activityId) {
      updates = {
        ...updates,
        doctorId: undefined,
        date: undefined,
        time: undefined,
      }
    }

    // Reset date/time only when changing to a different doctor
    if (updates.doctorId && updates.doctorId !== session.data.doctorId) {
      updates = {
        ...updates,
        date: undefined,
        time: undefined,
      }
    }

    const updatedSession: BookingSession = {
      ...session,
      step: updates.step || session.step,
      data: {
        ...session.data,
        ...{ ...updates, step: undefined },
      },
      metadata: {
        ...session.metadata,
        updatedAt: new Date().toISOString(),
      },
    }

    // Use the appropriate TTL based on booking type
    const ttl = updatedSession.data.isLastMinute ? LAST_MINUTE_SESSION_TTL : SESSION_TTL
    const key = `booking:${id}`

    await redis.set(key, updatedSession, { ex: ttl })
    console.log(`Updated session ${id}, step: ${updatedSession.step}`)

    return updatedSession
  } catch (error) {
    console.error(`Error updating session ${id}:`, error)
    Sentry.captureException(error, {
      extra: { function: 'updateBookingSession', sessionId: id, updates },
    })
    return null
  }
}

/**
 * Deletes a booking session
 * @param id Session ID
 */
export async function deleteBookingSession(id: string): Promise<void> {
  try {
    if (!isValidSessionId(id)) {
      console.error(`Invalid session ID format in delete: ${id}`)
      return
    }

    const key = `booking:${id}`
    await redis.del(key)
    console.log(`Deleted session: ${id}`)
  } catch (error) {
    console.error(`Error deleting session ${id}:`, error)
    Sentry.captureException(error, {
      extra: { function: 'deleteBookingSession', sessionId: id },
    })
  }
}
